/**
 * Spinner Component
 *
 * Small inline spinner for buttons, small elements, and inline loading states.
 * Does not block user interaction.
 *
 * Features:
 * - Inline spinner (uses border-current to inherit text color)
 * - Multiple size variants
 * - Lightweight and reusable
 *
 * @example
 * ```tsx
 * import { Spinner } from '@/components/ui/Loading';
 *
 * // In a button
 * <button>
 *   <Spinner size="sm" className="text-white" />
 *   Processing...
 * </button>
 * ```
 */

import { cn } from '@/lib/utils'

interface SpinnerProps {
  /** Size variant */
  size?: 'sm' | 'md' | 'lg'
  /** Custom className for additional styling (use to set color with text-* classes) */
  className?: string
}

const sizeClasses = {
  sm: 'w-3 h-3 border-2',
  md: 'w-4 h-4 border-2',
  lg: 'w-6 h-6 border-[3px]',
}

export function Spinner({ size = 'md', className = '' }: SpinnerProps) {
  return (
    <div
      className={cn(
        'inline-block rounded-full animate-spin border-current border-t-transparent',
        sizeClasses[size],
        className
      )}
      role="status"
      aria-label="Loading"
    >
      <span className="sr-only">Loading...</span>
    </div>
  )
}

export default Spinner
